import { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import axios from "axios";
import { API, toast } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Plus, Star, Trash2, TrendingUp, TrendingDown, 
  MoreVertical, Search, RefreshCw, Eye
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import { useRealtimeData } from "@/hooks/useRealtimeData";
import { useInstrumentSearch } from "@/hooks/useInstrumentSearch";
import InstrumentRow from "@/components/InstrumentRow";
import { StreamStatusBadge } from "@/components/StreamStatusBadge";
import { computeStreamUiStatus } from "@/realtime/statusLogic";
import { getDetailRoute } from "@/utils/assetRouting";
import { ConnectionBanner } from "@/components/StaleIndicator";
import { SmartAlertToggle } from "@/components/SmartAlertConfig";
import { useCriticalLogos } from "@/hooks/useCriticalLogos";

// Known MCX commodity base symbols for exchange detection
const MCX_SYMBOLS = new Set([
  'GOLD', 'GOLDM', 'GOLDPETAL', 'GOLDGUINEA', 'GOLDTEN',
  'SILVER', 'SILVERM', 'SILVERMIC',
  'CRUDEOIL', 'CRUDEOILM', 'NATURALGAS', 'NATGASMINI',
  'COPPER', 'ZINC', 'ZINCMINI', 'ALUMINIUM', 'ALUMINI',
  'LEAD', 'LEADMINI', 'NICKEL', 'COTTON', 'MENTHAOIL',
  'CARDAMOM', 'COTTONOIL', 'KAPAS', 'STEELREBAR',
  'MCXBULLDEX', 'MCXMETLDEX', 'ELECDMBL',
]);

// Known CDS/Forex symbols
const CDS_SYMBOLS = new Set([
  'USDINR', 'EURINR', 'GBPINR', 'JPYINR',
]);

// Regex to extract base name from full tradingsymbol (e.g., GOLD26APRFUT → GOLD, EURINR26306FUT → EURINR)
const FUT_RE = /^([A-Z]+?)(\d{2}[\dA-Z]{3}FUT)$/;
const OPT_RE = /^([A-Z]+?)(\d{2}[\dA-Z]{3}\d+(?:CE|PE))$/;

function getSymbolExchange(symbol) {
  const upper = symbol.toUpperCase();
  if (MCX_SYMBOLS.has(upper)) return 'MCX';
  if (CDS_SYMBOLS.has(upper)) return 'CDS';
  // Try extracting base from full tradingsymbol
  const m = upper.match(FUT_RE) || upper.match(OPT_RE);
  if (m) {
    const base = m[1];
    if (MCX_SYMBOLS.has(base)) return 'MCX';
    if (CDS_SYMBOLS.has(base)) return 'CDS';
    return 'NFO';
  }
  return 'NSE';
}

/**
 * WatchlistActions — Watchlist-specific action buttons (alert + dropdown).
 * Rendered as the `fallbackContent` of InstrumentRow's actions cell.
 */
const WatchlistActions = memo(({ symbol, onRemove, onNavigate, onNavigateTo }) => (
  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
    <SmartAlertToggle symbol={symbol} compact />
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`watchlist-actions-${symbol}`}>
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => onNavigate(symbol)} data-testid={`action-view-${symbol}`}>
          <Eye className="w-4 h-4 mr-2" />
          View Details
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onNavigateTo(`/option-chain?symbol=${symbol}`)} data-testid={`action-optionchain-${symbol}`}>
          <TrendingUp className="w-4 h-4 mr-2" />
          Option Chain
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onRemove(symbol)} className="text-red-600" data-testid={`action-remove-${symbol}`}>
          <Trash2 className="w-4 h-4 mr-2" />
          Remove
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
));

/**
 * WatchlistExtraCells — Exchange badge + stream status, shown between company and price columns.
 */
const WatchlistExtraCells = memo(({ symbol, resolveInfo, liveData, marketOpen }) => {
  const isExpired = resolveInfo?.status === 'expired';
  const isUnavailable = resolveInfo?.status === 'unavailable';
  const price = liveData?.last_price || 0;
  const tickStatus = computeStreamUiStatus({
    marketOpen,
    streamOk: liveData?.stream_ok !== false,
    ageMs: liveData?.age_ms ?? null,
    hasPrice: price > 0,
  });

  return (
    <td className="py-3 px-4">
      <div className="flex items-center gap-1.5 flex-wrap">
        {resolveInfo?.exchange && resolveInfo.exchange !== 'NSE' && (
          <Badge variant="outline" className="text-[10px] py-0 px-1">{resolveInfo.exchange}</Badge>
        )}
        {isExpired && <Badge variant="destructive" className="text-[10px] py-0 px-1">Expired</Badge>}
        {isUnavailable && <Badge variant="secondary" className="text-[10px] py-0 px-1">N/A</Badge>}
        {!isExpired && !isUnavailable && <StreamStatusBadge status={tickStatus} asOf={liveData?.as_of} compact />}
      </div>
    </td>
  );
});

export default function WatchlistPage() {
  const navigate = useNavigate();
  const [watchlists, setWatchlists] = useState([]);
  const [activeWatchlist, setActiveWatchlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newWatchlistName, setNewWatchlistName] = useState("");
  const [searchSymbol, setSearchSymbol] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAddSymbolDialog, setShowAddSymbolDialog] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);

  // Shared hook: instrument search for add-symbol dialog
  const { results: searchResults, loading: searchLoading, search: doSearch, clear: clearSearch } = useInstrumentSearch({ limit: 8 });

  // Resolved symbol metadata from backend (tradingsymbol, exchange, status)
  const [resolvedMap, setResolvedMap] = useState({});

  const symbols = useMemo(
    () => activeWatchlist?.symbols || [],
    [activeWatchlist]
  );

  // Batch-prefetch logos for all watchlist symbols
  useCriticalLogos(symbols, { critical: true });

  // Resolve all symbols via backend when watchlist changes
  useEffect(() => {
    if (!symbols.length) { setResolvedMap({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.post(`${API}/watchlist/resolve-symbols`, { symbols });
        if (!cancelled) setResolvedMap(resp.data?.resolved || {});
      } catch {
        // Fallback: use client-side detection
        const fallback = {};
        for (const s of symbols) {
          fallback[s] = { tradingsymbol: s, exchange: getSymbolExchange(s), status: 'active' };
        }
        if (!cancelled) setResolvedMap(fallback);
      }
    })();
    return () => { cancelled = true; };
  }, [symbols]);

  // Fix 3: Auto-remove expired contracts from watchlist after resolution
  const cleanupRanRef = useRef(false);
  useEffect(() => {
    if (!activeWatchlist || !Object.keys(resolvedMap).length) return;
    // Only run once per resolvedMap change
    if (cleanupRanRef.current) return;

    const expiredSymbols = symbols.filter(s => resolvedMap[s]?.status === 'expired');
    if (!expiredSymbols.length) return;

    cleanupRanRef.current = true;

    (async () => {
      for (const sym of expiredSymbols) {
        const info = resolvedMap[sym];
        try {
          await axios.delete(`${API}/watchlist/${activeWatchlist.id}/symbols/${sym}`);
          if (info?.suggested) {
            toast.info(`Removed expired ${sym}. Active contract: ${info.suggested}`);
          } else {
            toast.info(`Removed expired contract ${sym}`);
          }
        } catch {
          // silently ignore removal failures
        }
      }
      // Refresh watchlists to reflect removal
      try {
        const response = await axios.get(`${API}/watchlist`);
        const updated = response.data.watchlists || [];
        setWatchlists(updated);
        const refreshed = updated.find(w => w.id === activeWatchlist.id);
        if (refreshed) setActiveWatchlist(refreshed);
      } catch { /* ignore */ }
    })();
  }, [resolvedMap, activeWatchlist, symbols]);

  // Reset cleanup flag when watchlist changes
  useEffect(() => {
    cleanupRanRef.current = false;
  }, [activeWatchlist?.id]);

  // Fix 2b: Deduplicate — hide base symbols whose resolved tradingsymbol already exists as another entry
  const visibleSymbols = useMemo(() => {
    if (!symbols.length || !Object.keys(resolvedMap).length) return symbols;
    // Build a set of all tradingsymbols that are already directly in the list
    const directSymbols = new Set(symbols.map(s => s.toUpperCase()));
    return symbols.filter(sym => {
      const info = resolvedMap[sym];
      if (!info) return true;
      // If this is a base symbol that resolved to a different tradingsymbol...
      if (info.resolved_from && info.tradingsymbol) {
        // ...and that tradingsymbol is already directly in the list, hide this one
        if (directSymbols.has(info.tradingsymbol.toUpperCase()) && info.tradingsymbol.toUpperCase() !== sym.toUpperCase()) {
          return false;
        }
      }
      // Hide expired (they get auto-removed; don't show in the meantime)
      if (info.status === 'expired') return false;
      return true;
    });
  }, [symbols, resolvedMap]);

  // Group resolved tradingsymbols by exchange for multi-exchange WebSocket support
  const { nseSymbols, mcxSymbols, cdsSymbols, nfoSymbols, tsToOriginal } = useMemo(() => {
    const nse = [], mcx = [], cds = [], nfo = [];
    const mapping = {};  // tradingsymbol → original symbol
    for (const s of symbols) {
      const info = resolvedMap[s];
      if (!info || info.status === 'expired' || info.status === 'unavailable') continue;
      const ts = info.tradingsymbol || s;
      const ex = info.exchange || getSymbolExchange(s);
      mapping[ts] = s;
      if (ex === 'MCX') mcx.push(ts);
      else if (ex === 'CDS') cds.push(ts);
      else if (ex === 'NFO') nfo.push(ts);
      else nse.push(ts);
    }
    return { nseSymbols: nse, mcxSymbols: mcx, cdsSymbols: cds, nfoSymbols: nfo, tsToOriginal: mapping };
  }, [symbols, resolvedMap]);

  // WebSocket subscriptions per exchange
  const nseData = useRealtimeData(nseSymbols, 'NSE');
  const mcxData = useRealtimeData(mcxSymbols, 'MCX');
  const cdsData = useRealtimeData(cdsSymbols, 'CDS');
  const nfoData = useRealtimeData(nfoSymbols, 'NFO');

  // Merge ticks from all exchanges, mapping tradingsymbols back to original symbols
  const ticks = useMemo(() => {
    const allTicks = { ...nseData.ticks, ...mcxData.ticks, ...cdsData.ticks, ...nfoData.ticks };
    const mapped = {};
    for (const [ts, tick] of Object.entries(allTicks)) {
      const origSym = tsToOriginal[ts] || ts;
      mapped[origSym] = tick;
    }
    return mapped;
  }, [nseData.ticks, mcxData.ticks, cdsData.ticks, nfoData.ticks, tsToOriginal]);

  const isConnected = nseData.isConnected || mcxData.isConnected || cdsData.isConnected || nfoData.isConnected;
  const marketOpen = nseData.marketOpen || mcxData.marketOpen || cdsData.marketOpen || nfoData.marketOpen;

  // REST fallback: fetch initial tick data using resolved tradingsymbols
  const [restTicks, setRestTicks] = useState({});
  useEffect(() => {
    if (!symbols.length || !Object.keys(resolvedMap).length) return;
    const fetchInitialTicks = async () => {
      for (const sym of symbols) {
        const info = resolvedMap[sym];
        if (!info || info.status === 'expired' || info.status === 'unavailable') continue;
        const ts = info.tradingsymbol || sym;
        const exchange = info.exchange || getSymbolExchange(sym);
        try {
          const resp = await fetch(`${API}/realtime/tick/${ts}?exchange=${exchange}`);
          if (resp.ok) {
            const data = await resp.json();
            if (data?.last_price) {
              setRestTicks(prev => ({ ...prev, [sym]: data }));
            }
          }
        } catch { /* ignore — WS will provide data when available */ }
      }
    };
    fetchInitialTicks();
  }, [symbols, resolvedMap]);

  // Merge WebSocket ticks over REST fallback (WS takes priority for price)
  // But during market-closed, preserve REST-enriched volume/change
  const mergedTicks = useMemo(() => {
    const merged = { ...restTicks };
    for (const [sym, tick] of Object.entries(ticks)) {
      if (tick && (tick.last_price || tick.last_price === 0)) {
        const rest = restTicks[sym];
        if (rest && !marketOpen) {
          // Market closed: preserve enriched volume/change from REST if WS has 0
          const wsVol = tick.volume_traded || tick.volume || 0;
          const restVol = rest.volume_traded || rest.volume || 0;
          const wsChangePct = Math.abs(tick.change_percent || 0);
          const restChangePct = Math.abs(rest.change_percent || 0);
          merged[sym] = {
            ...rest,
            ...tick,
            // Keep REST enriched volume if WS volume is 0
            volume_traded: wsVol > 0 ? wsVol : restVol,
            volume: wsVol > 0 ? wsVol : restVol,
            // Keep REST enriched change if WS change is near-0
            change_percent: wsChangePct > 0.001 ? tick.change_percent : (restChangePct > 0.001 ? rest.change_percent : tick.change_percent),
            price_change: wsChangePct > 0.001 ? (tick.price_change || tick.change) : (restChangePct > 0.001 ? (rest.price_change || rest.change) : (tick.price_change || tick.change)),
          };
        } else {
          merged[sym] = tick;
        }
      }
    }
    return merged;
  }, [ticks, restTicks, marketOpen]);

  // Fetch watchlists
  const fetchWatchlists = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/watchlist`);
      setWatchlists(response.data.watchlists || []);
      if (response.data.watchlists?.length > 0 && !activeWatchlist) {
        setActiveWatchlist(response.data.watchlists[0]);
      }
    } catch (error) {
      console.error("Error fetching watchlists:", error);
    } finally {
      setLoading(false);
    }
  }, [activeWatchlist]);

  useEffect(() => {
    fetchWatchlists();
  }, [fetchWatchlists]);

  // Create new watchlist
  const createWatchlist = async () => {
    if (!newWatchlistName.trim()) {
      toast.error("Please enter a watchlist name");
      return;
    }
    try {
      const response = await axios.post(`${API}/watchlist`, {
        name: newWatchlistName,
        symbols: []
      });
      setWatchlists([...watchlists, response.data.watchlist]);
      setActiveWatchlist(response.data.watchlist);
      setNewWatchlistName("");
      setShowCreateDialog(false);
      toast.success("Watchlist created!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create watchlist");
    }
  };

  // Delete watchlist
  const deleteWatchlist = async (id) => {
    try {
      await axios.delete(`${API}/watchlist/${id}`);
      const updated = watchlists.filter(w => w.id !== id);
      setWatchlists(updated);
      if (activeWatchlist?.id === id) {
        setActiveWatchlist(updated[0] || null);
      }
      toast.success("Watchlist deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete watchlist");
    }
  };

  // Add symbol to watchlist
  const addSymbol = async () => {
    if (!searchSymbol.trim() || !activeWatchlist) return;
    try {
      await axios.post(`${API}/watchlist/${activeWatchlist.id}/symbols`, {
        symbol: searchSymbol.toUpperCase()
      });
      const updated = watchlists.map(w => {
        if (w.id === activeWatchlist.id) {
          return { ...w, symbols: [...(w.symbols || []), searchSymbol.toUpperCase()] };
        }
        return w;
      });
      setWatchlists(updated);
      setActiveWatchlist(updated.find(w => w.id === activeWatchlist.id));
      setSearchSymbol("");
      setShowAddSymbolDialog(false);
      toast.success(`Added ${searchSymbol.toUpperCase()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add symbol");
    }
  };

  // Remove symbol from watchlist
  const removeSymbol = async (symbol) => {
    if (!activeWatchlist) return;
    try {
      await axios.delete(`${API}/watchlist/${activeWatchlist.id}/symbols/${symbol}`);
      const updated = watchlists.map(w => {
        if (w.id === activeWatchlist.id) {
          return { ...w, symbols: w.symbols.filter(s => s !== symbol) };
        }
        return w;
      });
      setWatchlists(updated);
      setActiveWatchlist(updated.find(w => w.id === activeWatchlist.id));
      toast.success(`Removed ${symbol}`);
    } catch (error) {
      toast.error("Failed to remove symbol");
    }
  };

  const handleNavigate = useCallback((symbol) => {
    navigate(getDetailRoute(symbol));
  }, [navigate]);

  const handleNavigateTo = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  // Compute top gainer/loser from live data
  // Fix 2a: Only include stocks with valid prices (last_price > 0)
  const { topGainer, topLoser } = useMemo(() => {
    const entries = Object.entries(mergedTicks);
    if (!entries.length) return { topGainer: null, topLoser: null };
    
    let gainer = null, loser = null;
    for (const [sym, tick] of entries) {
      // Skip stocks without valid price data to avoid -100% bug
      if (!tick || !tick.last_price || tick.last_price <= 0) continue;
      
      const pct = tick?.change_percent || 0;
      if (!gainer || pct > (gainer.change_percent || 0)) gainer = { symbol: sym, change_percent: pct };
      if (!loser || pct < (loser.change_percent || 0)) loser = { symbol: sym, change_percent: pct };
    }
    return { topGainer: gainer, topLoser: loser };
  }, [mergedTicks]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#00b386]" />
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title="Watchlist" 
        description="Track your favorite stocks with multiple watchlists"
        path="/watchlist"
      />
      <div className="space-y-6 pb-8 max-w-screen-2xl mx-auto">
        {/* WS Connection Banner */}
        <ConnectionBanner isConnected={isConnected} streamStatus={isConnected ? 'live' : 'reconnecting'} />
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Watchlist</h1>
            <p className="text-gray-600 mt-1">Track your favorite stocks in real-time</p>
          </div>
          <div className="flex gap-2 items-center">
            <Badge 
              data-testid="ws-status-badge"
              variant="outline" 
              className={`text-xs ${isConnected ? 'border-green-500 text-green-600' : 'border-gray-400 text-gray-500'}`}
            >
              {isConnected ? 'Live' : 'Connecting...'}
            </Badge>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#00b386] hover:bg-[#009970] rounded-xl">
                  <Plus className="w-4 h-4 mr-2" />
                  New Watchlist
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Watchlist</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Input
                    placeholder="Watchlist name"
                    value={newWatchlistName}
                    onChange={(e) => setNewWatchlistName(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && createWatchlist()}
                    data-testid="new-watchlist-name-input"
                  />
                  <Button onClick={createWatchlist} className="w-full bg-[#00b386]" data-testid="create-watchlist-btn">
                    Create
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Watchlist Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {watchlists.map((wl) => (
            <Button
              key={wl.id}
              variant={activeWatchlist?.id === wl.id ? "default" : "outline"}
              className={`rounded-xl whitespace-nowrap ${
                activeWatchlist?.id === wl.id ? "bg-[#00b386]" : ""
              }`}
              onClick={() => setActiveWatchlist(wl)}
              data-testid={`watchlist-tab-${wl.name}`}
            >
              <Star className={`w-4 h-4 mr-2 ${wl.is_default ? "fill-yellow-400" : ""}`} />
              {wl.name}
              <Badge variant="secondary" className="ml-2">
                {wl.symbols?.length || 0}
              </Badge>
            </Button>
          ))}
        </div>

        {/* Active Watchlist */}
        {activeWatchlist && (
          <Card className="border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-[#00b386]" />
                {activeWatchlist.name}
              </CardTitle>
              <div className="flex gap-2">
                <Dialog open={showAddSymbolDialog} onOpenChange={setShowAddSymbolDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-xl" data-testid="add-stock-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Stock
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Stock to Watchlist</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search stocks (e.g., RELIANCE, TCS)"
                          className="pl-10"
                          value={searchSymbol}
                          onChange={(e) => { setSearchSymbol(e.target.value); doSearch(e.target.value); }}
                          onKeyPress={(e) => e.key === "Enter" && addSymbol()}
                          data-testid="add-symbol-input"
                        />
                      </div>
                      {/* Search suggestions from useInstrumentSearch */}
                      {searchResults.length > 0 && searchSymbol.length > 0 && (
                        <div className="max-h-48 overflow-y-auto border rounded-lg divide-y" data-testid="search-suggestions">
                          {searchResults.map((r, i) => (
                            <button
                              key={r.symbol || r.tradingsymbol || i}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 flex justify-between items-center"
                              onClick={() => { setSearchSymbol(r.symbol || r.tradingsymbol); clearSearch(); }}
                              data-testid={`search-result-${r.symbol || r.tradingsymbol}`}
                            >
                              <div>
                                <span className="font-medium text-sm">{r.symbol || r.tradingsymbol}</span>
                                {r.name && <span className="text-xs text-gray-500 ml-2">{r.name}</span>}
                              </div>
                              {r.exchange && <span className="text-xs text-gray-400">{r.exchange}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      {searchLoading && searchSymbol.length > 0 && (
                        <p className="text-sm text-gray-400 text-center">Searching...</p>
                      )}
                      <Button onClick={addSymbol} className="w-full bg-[#00b386]" data-testid="confirm-add-symbol-btn">
                        Add to Watchlist
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                {!activeWatchlist.is_default && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-500 hover:text-red-700 rounded-xl"
                    onClick={() => deleteWatchlist(activeWatchlist.id)}
                    data-testid="delete-watchlist-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {visibleSymbols.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Star className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No stocks in this watchlist</p>
                  <p className="text-sm">Click Add Stock to start tracking</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="watchlist-table">
                    <thead>
                      <tr className="text-sm font-medium text-gray-500 border-b">
                        <th className="text-left py-2 px-4">Stock</th>
                        <th className="text-left py-2 px-4">Status</th>
                        <th className="text-right py-2 px-4">Price</th>
                        <th className="text-right py-2 px-4">Change</th>
                        <th className="text-right py-2 px-4">Volume</th>
                        <th className="py-2 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleSymbols.map((symbol, idx) => {
                        const tick = mergedTicks[symbol] || {};
                        const resolveInfo = resolvedMap[symbol];
                        const item = {
                          symbol,
                          name: resolveInfo?.name || resolveInfo?.tradingsymbol || symbol,
                          price: tick.last_price || 0,
                          change: tick.price_change || tick.change || 0,
                          change_percent: tick.change_percent || 0,
                          volume: tick.volume_traded || tick.volume || 0,
                          _live: !!tick.last_price,
                        };
                        return (
                          <InstrumentRow
                            key={symbol}
                            item={item}
                            hovered={hoveredRow === symbol}
                            onHover={setHoveredRow}
                            onClick={() => handleNavigate(symbol)}
                            isConnected={isConnected}
                            showVolume
                            showActions
                            index={idx}
                            testIdPrefix="watchlist"
                            extraCells={
                              <WatchlistExtraCells
                                symbol={symbol}
                                resolveInfo={resolveInfo}
                                liveData={tick}
                                marketOpen={marketOpen}
                              />
                            }
                            fallbackContent={
                              <WatchlistActions
                                symbol={symbol}
                                onRemove={removeSymbol}
                                onNavigate={handleNavigate}
                                onNavigateTo={handleNavigateTo}
                              />
                            }
                          />
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Top Gainer</p>
                  <p className="text-2xl font-bold text-green-600" data-testid="top-gainer">
                    {topGainer?.symbol || "—"}
                  </p>
                  {topGainer && <p className="text-xs text-green-500">+{topGainer.change_percent?.toFixed(2)}%</p>}
                </div>
                <TrendingUp className="w-10 h-10 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-lg bg-gradient-to-br from-red-50 to-red-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Top Loser</p>
                  <p className="text-2xl font-bold text-red-600" data-testid="top-loser">
                    {topLoser?.symbol || "—"}
                  </p>
                  {topLoser && <p className="text-xs text-red-500">{topLoser.change_percent?.toFixed(2)}%</p>}
                </div>
                <TrendingDown className="w-10 h-10 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Stocks</p>
                  <p className="text-2xl font-bold text-blue-600" data-testid="total-stocks">
                    {watchlists.reduce((acc, wl) => acc + (wl.symbols?.length || 0), 0)}
                  </p>
                </div>
                <Star className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
